<template>
    <div>
        <div v-for="task in tasks" :key="task.id">
            <float-on-mask tag="div" :float-up="isBriefAccepted">
                <g-collapsible-card :title="task.name" :active="panelsOpen" no-padding class="collapsible-card">
                    <div class="extended-card-header">
                        <g-media-object>
                            <template #left>
                                <activation-task-icon :task="task" />
                            </template>
                            <template #default>
                                <g-typography tag="span" size="small" :value="getLabel(task)" />
                            </template>
                            <template #right>
                                <g-typography
                                    v-if="isCreatorRequired(task)"
                                    tag="span"
                                    size="small"
                                    color="error"
                                    value="(Creator/Business Account Required)"
                                />
                            </template>
                        </g-media-object>

                        <g-progress
                            stroke-color="#303C4A"
                            :percent="getTaskProgress(task)"
                            :format="formatTaskProgress(task)"
                        />
                    </div>

                    <div
                        v-if="task.requires_approval"
                        :class="{
                            'brand-approval-warning--padding': true,
                            'brand-approval-warning--border': !!task.description
                        }"
                    >
                        <g-media-object>
                            <template #left>
                                <g-icon name="exclamation-circle" color="orange-5" size="small" />
                            </template>
                            <template #default>
                                <g-typography tag="div" size="small">
                                    Brand approval is required for this task
                                </g-typography>
                                <g-typography tag="div" color="grey-6" size="small">
                                    {{ deliverablesInReview(task) }} in review ·
                                    {{ deliverablesApproved(task) }} approved ·
                                    {{ deliverablesCompleted(task) }} complete
                                </g-typography>
                            </template>
                        </g-media-object>
                    </div>
                    <div
                        v-if="task.description"
                        class="card-content"
                        data-testid="activation_task_card__description_box"
                        v-html="$sanitize(task.description)"
                    />

                    <template #footer>
                        <template v-if="isBriefAccepted && canDeliver(task)">
                            <g-button
                                v-if="getIsContentManualStatsEnabled(task)"
                                shape="round"
                                theme="live"
                                block
                                type="default"
                                data-testid="view-button"
                                @click="goToDelivery(task)"
                            >
                                Update
                            </g-button>
                            <g-button
                                v-else
                                shape="round"
                                theme="live"
                                block
                                type="primary"
                                data-testid="view-button"
                                @click="goToDelivery(task)"
                            >
                                Deliver
                            </g-button>
                        </template>
                        <g-button
                            v-else-if="!task.is_required && !isBriefAccepted && !optional"
                            shape="round"
                            theme="live"
                            block
                            @click="$emit('remove-task', task.id)"
                        >
                            Remove
                        </g-button>
                        <g-button
                            v-else-if="!task.is_required && !isBriefAccepted && optional"
                            shape="round"
                            theme="live"
                            block
                            @click="$emit('add-task', task.id)"
                        >
                            Add
                        </g-button>
                    </template>
                </g-collapsible-card>
            </float-on-mask>
        </div>
    </div>
</template>

<script>
    import { computed } from 'vue-demi';
    import { get } from 'lodash';
    import { TIKTOK_ID, TYPE_INSTAGRAM_REEL, TYPE_INSTAGRAM_STORY } from '../../../../constants/networks';
    import {
        CONTENT_APPROVAL_STATUS_REVIEW,
        CONTENT_APPROVAL_STATUS_APPROVED
    } from '../../../../constants/content-approval';
    import { EVENT_NAME_CONTENT_APPROVAL_VIEW_DELIVERABLES_LIST } from '../../../../constants/analytics-events';
    import ActivationBriefMixin from '../mixins/ActivationBriefMixin';
    import ActivationTasksMixin from '../mixins/ActivationTasksMixin';
    import FloatOnMask from '../../../library/FloatOnMask.vue';
    import ActivationTaskIcon from '../partials/tasks/ActivationTaskIcon.vue';
    import useTaskDelivery from '../composables/use-task-delivery';
    import useEventTracking from '../../../../../src/composables/use-event-tracking';
    import useNetworks from '../../../../../src/composables/use-networks';
    import { useFeatureFlagsStore } from '../../../../../stores';

    const getTaskProgress = (task) => {
        const percent = (task.complete_quantity / (task.quantity !== 0 ? task.quantity : 1)) * 100;
        return percent > 100 ? 100 : percent;
    };

    export default {
        name: 'ActivationTaskCard',
        components: {
            FloatOnMask,
            ActivationTaskIcon
        },
        mixins: [ActivationBriefMixin, ActivationTasksMixin],
        props: {
            brief: {
                type: Object,
                required: true
            },
            tasks: {
                type: Array,
                required: true
            },
            panelsOpen: {
                type: Boolean,
                default: false
            },
            optional: {
                type: Boolean,
                default: false
            },
            context: {
                type: String,
                default: ''
            }
        },
        methods: {
            isCreatorRequired(task) {
                return useNetworks().isInstagramGraphRequiredForActivationTask(task);
            },
            deliverablesCompleted(task) {
                const completed = this.deliverables.filter(
                    (deliverable) => deliverable.task_id === task.id && deliverable.is_complete
                );
                return completed?.length;
            },
            deliverablesInReview(task) {
                const inReview = this.deliverables.filter(
                    (deliverable) =>
                        deliverable.task_id === task.id &&
                        deliverable.approval_status === CONTENT_APPROVAL_STATUS_REVIEW
                );
                return inReview?.length;
            },
            deliverablesApproved(task) {
                const approved = this.deliverables.filter(
                    (deliverable) =>
                        deliverable.task_id === task.id &&
                        deliverable.approval_status === CONTENT_APPROVAL_STATUS_APPROVED &&
                        !deliverable.is_complete
                );
                return approved?.length;
            },
            getTaskProgress: getTaskProgress,
            formatTaskProgress(task) {
                return (percent) => `${task.complete_quantity}/${task.quantity !== 0 ? task.quantity : '∞'} complete`;
            },
            hasNetwork(task) {
                return task.hasOwnProperty('type');
            },
            canDeliver(task) {
                return !this.isNetworkMarketplace(task) && this.hasNetwork(task);
            },
            isNetworkMarketplace(task) {
                return this.brief.has_module_creator_licensing && task.network_id === TIKTOK_ID;
            }
        },

        setup(props) {
            const events = useEventTracking();
            const { goToDeliveryPage } = useTaskDelivery();
            const { isMetaById } = useNetworks();

            const isContentManualStatsEnabled = computed(
                () => useFeatureFlagsStore().data['content-manual-stats-enabled'] || false
            );

            const getIsContentManualStatsEnabled = (task) => {
                return (
                    (isMetaById(task.network_id) && getTaskProgress(task) > 0 && isContentManualStatsEnabled.value) ||
                    false
                );
            };

            const deliverables = computed(() => {
                return get(props.brief, 'deliverables', []);
            });

            const goToDelivery = (taskSelected) => {
                events.trackEvent(
                    EVENT_NAME_CONTENT_APPROVAL_VIEW_DELIVERABLES_LIST,
                    getViewDeliverablesClickedEventPayload()
                );
                goToDeliveryPage(taskSelected, false, firstDeliverable(taskSelected), {
                    back_route: 'v2.activation.brief.tasks',
                    context: props.context
                });
            };

            const firstDeliverable = (task) => {
                return deliverables.value.find((deliverable) => deliverable.task_id === task.id);
            };

            const getViewDeliverablesClickedEventPayload = () => {
                return {
                    activation_id: props.brief.activation.id,
                    contact_id: props.brief.contact_id
                };
            };

            return {
                goToDelivery,
                deliverables,
                firstDeliverable,
                goToDeliveryPage,
                getIsContentManualStatsEnabled
            };
        }
    };
</script>

<style lang="scss" scoped>
    .margin-bottom-small {
        margin-bottom: $g-spacing-3;
    }

    .margin-bottom-normal {
        margin-bottom: $g-spacing-5;
    }

    .margin-bottom-large {
        margin-bottom: $g-spacing-6;
    }

    .break-new-line {
        white-space: pre-line;
    }

    .collapsible-card,
    ::v-deep .ant-collapse,
    ::v-deep .ant-collapse-header {
        border-radius: $g-border-radius-lg !important;
    }

    .collapsible-card {
        &:last-child {
            margin-bottom: $g-spacing-6;
        }

        ::v-deep .ant-collapse,
        ::v-deep .ant-collapse-item,
        ::v-deep .footer {
            background-color: transparent;
        }

        ::v-deep .ant-collapse-header {
            background-color: $g-color-grey-2 !important;
            padding: $g-spacing-5 $g-spacing-8 $g-spacing-5 $g-spacing-5 !important;

            .ant-collapse-arrow {
                right: $g-spacing-5;
                left: auto !important;
                color: $g-color-grey-6 !important;
            }
        }

        .extended-card-header {
            background-color: $g-color-grey-2;
            border-bottom: 1px solid $g-color-grey-4;
            padding: 0 $g-spacing-4 $g-spacing-4;

            ::v-deep .ant-progress {
                padding-left: $g-spacing-2;
            }

            ::v-deep .ant-progress-inner {
                background-color: $g-color-grey-4;
            }

            $progress-text-width: 80px;

            ::v-deep .ant-progress-outer {
                margin-right: calc((-1 * $progress-text-width) - $g-spacing-3);
                padding-right: calc($progress-text-width + $g-spacing-3);
            }

            ::v-deep .ant-progress-text {
                width: $progress-text-width;
                font-size: $g-font-size-small;
                color: $g-color-grey-6;
            }
        }

        ::v-deep .ant-collapse-content {
            border-top: 1px solid $g-color-grey-2 !important;
        }

        .card-content {
            padding: $g-spacing-5 $g-spacing-5 0;
        }

        .brand-approval-warning--padding {
            padding: $g-spacing-5;
        }
        .brand-approval-warning--border {
            border-bottom: 1px solid $g-color-grey-4;
        }

        ::v-deep .footer {
            padding: $g-spacing-5;
        }
    }
</style>
